<template>
  <section class="workspace-grid workspace-grid--split event-edit-shell">
    <article class="glass-surface workspace-panel event-edit-panel">
      <div class="workspace-panel__header event-edit-panel__header">
        <div>
          <span class="workspace-kicker">Editar evento</span>
          <h2 class="workspace-title">Actualiza la identidad y salida del evento.</h2>
          <p class="workspace-copy">Foto, nombre, fechas, venue y copy en el mismo panel. Por ahora todos los cambios se aplican de inmediato.</p>
        </div>

        <span class="workspace-status-pill" :class="policyPillClass">{{ policyLabel }}</span>
      </div>

      <div class="workspace-badge-row workspace-badge-row--compact event-edit-policy-row">
        <span class="workspace-badge">
          <i class="fas fa-bolt"></i>
          <span>{{ eventPolicy.soft_fields.length }} campos soft</span>
        </span>
        <span class="workspace-badge">
          <i class="fas fa-shield-alt"></i>
          <span>{{ eventPolicy.sensitive_fields.length }} sensibles, hoy tambien directos</span>
        </span>
      </div>

      <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
      <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

      <form class="workspace-form event-edit-form" @submit.prevent="submitChanges">
        <section class="event-edit-section">
          <div class="event-edit-section__header">
            <div>
              <small>Identidad</small>
              <strong>Lo que la gente ve primero</strong>
            </div>
            <span class="event-edit-section__tag">Portada, nombre y copy</span>
          </div>

          <div class="workspace-form workspace-form--two">
            <label class="workspace-field workspace-field--wide">
              <span>Nombre</span>
              <input v-model="form.name" class="workspace-input" type="text" placeholder="Neon Ritual 2026" required />
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Descripcion</span>
              <textarea v-model="form.description" class="workspace-input workspace-input--textarea" rows="5" placeholder="Explica que es el evento, para quien es y que necesita saber la gente antes de comprar."></textarea>
            </label>

            <label class="workspace-field">
              <span>Salida</span>
              <select v-model.number="form.type" class="workspace-input workspace-select">
                <option :value="1">Publico</option>
                <option :value="2">Privado</option>
                <option :value="3">No publicar aun</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Acceso</span>
              <select v-model.number="form.access" class="workspace-input workspace-select">
                <option :value="1">Publico</option>
                <option :value="2">Privado con link</option>
                <option :value="3">Privado con codigo</option>
                <option :value="5">POS</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Inicio</span>
              <input v-model="form.start_date" class="workspace-input" type="datetime-local" required />
            </label>

            <label class="workspace-field">
              <span>Fin</span>
              <input v-model="form.end_date" class="workspace-input" type="datetime-local" required />
            </label>
          </div>
        </section>

        <section class="event-edit-section">
          <div class="event-edit-section__header">
            <div>
              <small>Contexto</small>
              <strong>Categoria, ciudad y venue</strong>
            </div>
            <span class="event-edit-section__tag">Ayuda a orientar el checkout</span>
          </div>

          <div class="workspace-form workspace-form--two">
            <label class="workspace-field">
              <span>Categoria</span>
              <select v-model="form.category" class="workspace-input workspace-select" :disabled="loadingOptions">
                <option value="">{{ loadingOptions ? 'Cargando categorias...' : 'Selecciona una categoria' }}</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Pais</span>
              <select v-model="form.country" class="workspace-input workspace-select" :disabled="loadingOptions" @change="handleCountryChange">
                <option value="">{{ loadingOptions ? 'Cargando paises...' : 'Selecciona un pais' }}</option>
                <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Ciudad</span>
              <select v-model="form.city" class="workspace-input workspace-select" :disabled="loadingOptions || !form.country" @change="handleCityChange">
                <option value="">{{ !form.country ? 'Selecciona un pais primero' : (loadingOptions ? 'Cargando ciudades...' : 'Selecciona una ciudad') }}</option>
                <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.name }}</option>
              </select>
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Venue</span>
              <select v-model="form.venue" class="workspace-input workspace-select" :disabled="loadingOptions || loadingVenues || !form.city">
                <option value="">{{ venuePlaceholder }}</option>
                <option v-for="venue in filteredVenues" :key="venue.id" :value="venue.id">{{ venue.location_label ? `${venue.name} - ${venue.location_label}` : venue.name }}</option>
              </select>
              <small class="workspace-field__note">{{ venueHelperText }}</small>
            </label>

            <div class="workspace-field workspace-field--wide">
              <span>Mostrar fecha final</span>
              <label class="workspace-inline-check">
                <input v-model="form.show_end_date" type="checkbox" />
                <span>Mostrar inicio y cierre en el checkout</span>
              </label>
            </div>

            <label class="workspace-field">
              <span>Moneda de cobro</span>
              <select v-model="form.presentment_currency" class="workspace-input workspace-select" :disabled="!form.country">
                <option value="">{{ form.country ? 'Selecciona una moneda' : 'Selecciona un pais primero' }}</option>
                <option v-for="currency in availableCurrencies" :key="currency.code" :value="currency.code">{{ currency.code }} - {{ currency.label }}</option>
              </select>
            </label>

            <label class="workspace-field">
              <span>Politica de payout</span>
              <select v-model="form.payout_mode" class="workspace-input workspace-select">
                <option v-for="option in payoutModeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </label>

            <label v-if="form.payout_mode === 'after_event_plus_buffer' || form.payout_mode === 'split_60_40_event'" class="workspace-field">
              <span>Buffer post-evento (horas)</span>
              <input v-model.number="form.post_event_buffer_hours" class="workspace-input" type="number" min="0" step="1" />
            </label>
          </div>
        </section>

        <section class="event-edit-section">
          <div class="event-edit-section__header">
            <div>
              <small>Media y notas</small>
              <strong>Afina la presentacion</strong>
            </div>
            <span class="event-edit-section__tag">Todo impacta de inmediato</span>
          </div>

          <div class="workspace-form workspace-form--two">
            <div class="workspace-field workspace-field--wide">
              <span>Portada</span>
              <input ref="bannerInput" class="event-edit-file-input" type="file" accept="image/*" @change="onBannerChange" />
              <button class="event-edit-banner" :class="{ 'has-image': Boolean(bannerPreview), 'is-dragging': dragBannerActive }" type="button" @click="triggerBannerPicker" @dragover.prevent="handleBannerDrag" @dragleave.prevent="handleBannerDragLeave" @drop.prevent="handleBannerDrop">
                <img v-if="bannerPreview" :src="bannerPreview" :alt="form.name || event.name || 'Preview del evento'" class="event-edit-banner__image" />
                <div v-else class="event-edit-banner__placeholder">
                  <i class="fas fa-image"></i>
                  <strong>Sube o arrastra una portada</strong>
                  <small>PNG o JPG menor a 10 MB</small>
                </div>
                <div class="event-edit-banner__overlay"></div>
                <div class="event-edit-banner__copy">
                  <span>{{ bannerPreview ? 'Cambiar portada' : 'Elegir portada' }}</span>
                  <small>{{ bannerPreview ? 'Haz click para reemplazarla' : 'Se vera en el hero y en el checkout' }}</small>
                </div>
              </button>

              <div class="workspace-actions workspace-actions--compact event-edit-banner__actions">
                <button class="workspace-button workspace-button--small" type="button" @click="triggerBannerPicker">
                  <i class="fas fa-image"></i>
                  <span>{{ bannerPreview ? 'Cambiar foto' : 'Subir foto' }}</span>
                </button>
                <button v-if="bannerPreview" class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="clearBanner">
                  <i class="fas fa-xmark"></i>
                  <span>Quitar</span>
                </button>
              </div>
            </div>

            <label class="workspace-field">
              <span>Trailer</span>
              <input v-model="form.trailer" class="workspace-input" type="text" placeholder="https://youtube.com/..." />
            </label>

            <label class="workspace-field">
              <span>Meta Pixel</span>
              <input v-model="form.meta_pixel" class="workspace-input" type="text" placeholder="ID del pixel" />
            </label>

            <label class="workspace-field workspace-field--wide">
              <span>Mensaje interno</span>
              <textarea v-model="form.message" class="workspace-input workspace-input--textarea" rows="4" placeholder="Notas internas para el equipo FOMO o para el setup del evento."></textarea>
            </label>
          </div>
        </section>

        <div class="workspace-actions workspace-actions--end event-edit-actions">
          <button class="workspace-button workspace-button--small workspace-button--ghost" type="button" :disabled="saving || !isDirty" @click="resetForm">
            <i class="fas fa-rotate-left"></i>
            <span>Restaurar</span>
          </button>
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving || !canSubmit">
            <i class="fas fa-save"></i>
            <span>{{ saving ? 'Guardando...' : 'Guardar cambios' }}</span>
          </button>
        </div>
      </form>
    </article>

    <aside class="glass-surface workspace-panel event-edit-preview">
      <span class="workspace-kicker">Preview</span>
      <div class="event-edit-preview__media" :class="{ 'is-empty': !bannerPreview }">
        <img v-if="bannerPreview" :src="bannerPreview" :alt="form.name || event.name || 'Preview del evento'" />
        <div v-else class="event-edit-preview__placeholder">
          <i class="fas fa-star"></i>
          <strong>{{ form.name || event.name || 'Tu evento' }}</strong>
        </div>
      </div>

      <div class="event-edit-preview__copy">
        <h3>{{ form.name || event.name || 'Tu evento' }}</h3>
        <p>{{ form.description || event.description || 'Agrega una descripcion clara para que el evento se entienda mejor.' }}</p>
      </div>

      <div class="workspace-badge-row workspace-badge-row--compact">
        <span class="workspace-badge"><i class="fas fa-calendar-alt"></i>{{ previewDateRange }}</span>
        <span class="workspace-badge"><i class="fas fa-map-marker-alt"></i>{{ previewVenueLabel }}</span>
        <span class="workspace-badge"><i class="fas fa-shapes"></i>{{ previewCategoryLabel }}</span>
        <span class="workspace-badge"><i class="fas fa-coins"></i>{{ form.presentment_currency || event.presentment_currency || 'USD' }}</span>
      </div>

      <div class="event-edit-preview__note">
        <strong>Modo actual</strong>
        <p>Los cambios soft y sensibles quedan activos apenas guardas. La clasificacion ya queda lista para approval futuro.</p>
      </div>

      <div class="event-edit-preview__fields">
        <small>Campos sensibles preparados</small>
        <div class="workspace-chip-row">
          <span v-for="field in visibleSensitiveFields" :key="field" class="workspace-chip">{{ humanizeField(field) }}</span>
        </div>
      </div>
    </aside>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'

const PAYOUT_MODE_OPTIONS = [
  { value: 'after_event_plus_buffer', label: 'Luego del evento + buffer' },
  { value: 'split_60_40_event', label: '60% antes / 40% despues' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'on_request', label: 'Por solicitud del organizador' },
  { value: 'manual', label: 'Manual / tesoreria' },
]

const DEFAULT_EVENT_EDIT_POLICY = {
  approval_mode: 'auto',
  soft_fields: ['description', 'banner_url', 'trailer', 'meta_pixel', 'message', 'show_end_date', 'fomo_safe_enabled'],
  sensitive_fields: ['name', 'start_date', 'end_date', 'city', 'venue', 'category', 'type', 'access', 'fee_bearer'],
}

const FIELD_LABELS = {
  name: 'nombre',
  description: 'descripcion',
  banner_url: 'portada',
  trailer: 'trailer',
  meta_pixel: 'meta pixel',
  message: 'mensaje interno',
  show_end_date: 'fecha final visible',
  fomo_safe_enabled: 'Fomo Safe',
  start_date: 'fecha de inicio',
  end_date: 'fecha de cierre',
  city: 'ciudad',
  venue: 'venue',
  category: 'categoria',
  type: 'salida',
  access: 'acceso',
  fee_bearer: 'quien cubre fee',
}

export default {
  name: 'EventEditPanel',

  props: {
    event: {
      type: Object,
      required: true,
    },
    eventId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      countries: [],
      categories: [],
      cities: [],
      venues: [],
      venuesByCity: {},
      venueRequestToken: 0,
      cityRequestToken: 0,
      loadingOptions: true,
      loadingVenues: false,
      saving: false,
      errorMessage: '',
      successMessage: '',
      dragBannerActive: false,
      bannerPreview: '',
      bannerObjectUrl: '',
      removeBanner: false,
      syncedSignature: '',
      lastHydratedSignature: '',
      form: {
        name: '',
        description: '',
        type: 1,
        access: 1,
        start_date: '',
        end_date: '',
        category: '',
        country: '',
        city: '',
        venue: '',
        presentment_currency: '',
        payout_mode: 'after_event_plus_buffer',
        post_event_buffer_hours: 24,
        trailer: '',
        meta_pixel: '',
        message: '',
        show_end_date: false,
        banner: null,
      },
    }
  },

  computed: {
    eventPolicy() {
      const policy = this.event && this.event.event_edit_policy
      return policy && Array.isArray(policy.soft_fields) && Array.isArray(policy.sensitive_fields) ? policy : DEFAULT_EVENT_EDIT_POLICY
    },

    selectedCategory() {
      return this.categories.find((category) => Number(category.id) === Number(this.form.category)) || null
    },

    selectedCountry() {
      return this.countries.find((country) => Number(country.id) === Number(this.form.country)) || null
    },

    selectedCity() {
      return this.cities.find((city) => Number(city.id) === Number(this.form.city)) || null
    },

    selectedVenue() {
      return this.venues.find((venue) => Number(venue.id) === Number(this.form.venue)) || null
    },

    filteredVenues() {
      return this.venues
    },

    availableCurrencies() {
      return this.selectedCountry && Array.isArray(this.selectedCountry.available_currencies)
        ? this.selectedCountry.available_currencies
        : []
    },

    payoutModeOptions() {
      return PAYOUT_MODE_OPTIONS
    },

    venuePlaceholder() {
      if (this.loadingOptions || this.loadingVenues) {
        return 'Cargando venues...'
      }

      if (!this.form.city) {
        return 'Selecciona una ciudad primero'
      }

      if (this.venues.length === 0 || this.filteredVenues.length === 0) {
        return 'No hay venues cargados para esa ciudad'
      }

      return 'Selecciona un venue'
    },

    venueHelperText() {
      if (!this.form.city) {
        return 'Elige la ciudad primero para cargar solo los venues que correspondan.'
      }

      if (this.loadingVenues) {
        return `Cargando venues para ${this.selectedCity ? this.selectedCity.name : 'la ciudad'}...`
      }

      if (this.venues.length === 0) {
        return `No hay venues cargados para ${this.selectedCity ? this.selectedCity.name : 'esa ciudad'}.`
      }

      return `${this.filteredVenues.length} venues disponibles para ${this.selectedCity ? this.selectedCity.name : 'la ciudad seleccionada'}`
    },

    previewDateRange() {
      if (!this.form.start_date && !this.form.end_date) {
        return 'Por definir'
      }

      const start = this.formatPreviewDate(this.form.start_date)
      const end = this.formatPreviewDate(this.form.end_date)

      if (start && end) {
        return `${start} - ${end}`
      }

      return start || end || 'Por definir'
    },

    previewVenueLabel() {
      const activeVenue = this.selectedVenue || this.event.venue_detail || this.event.venue || null
      if (activeVenue && activeVenue.name) {
        return activeVenue.name
      }
      return 'Venue pendiente'
    },

    previewCategoryLabel() {
      const activeCategory = this.selectedCategory || this.event.category_detail || this.event.category || null
      if (activeCategory && activeCategory.name) {
        return activeCategory.name
      }
      return 'Categoria por definir'
    },

    visibleSensitiveFields() {
      return this.eventPolicy.sensitive_fields.slice(0, 6)
    },

    isDirty() {
      return this.captureFormSignature() !== this.syncedSignature
    },

    canSubmit() {
      return Boolean(this.form.name && this.form.start_date && this.form.end_date)
    },

    policyLabel() {
      return this.eventPolicy.approval_mode === 'auto' ? 'Aplicacion inmediata' : 'Revision manual'
    },

    policyPillClass() {
      return this.eventPolicy.approval_mode === 'auto' ? 'is-active' : 'is-warning'
    },
  },

  created() {
    this.bootstrapOptions()
  },

  beforeUnmount() {
    this.revokeBannerPreview()
  },

  watch: {
    event: {
      immediate: true,
      deep: true,
      handler(value) {
        const nextSignature = this.computeEventEditableSignature(value)
        if (nextSignature === this.lastHydratedSignature) {
          return
        }

        this.lastHydratedSignature = nextSignature
        this.hydrateFormFromEvent(value)
      },
    },
  },

  methods: {
    normalizeCollection(payload) {
      if (Array.isArray(payload)) {
        return payload
      }

      if (payload && Array.isArray(payload.results)) {
        return payload.results
      }

      return []
    },

    normalizeVenues(payload) {
      return this.normalizeCollection(payload).map((venue) => ({
        ...venue,
        city_id: venue.city_id || (venue.city && typeof venue.city === 'object' ? venue.city.id : venue.city) || null,
        zone_id: venue.zone_id || (venue.zone && typeof venue.zone === 'object' ? venue.zone.id : venue.zone) || null,
      })).sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
    },

    normalizeCountries(payload) {
      return this.normalizeCollection(payload).sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
    },

    extractRelationId(value, fallback = '') {
      if (value == null || value === '') {
        return fallback
      }

      if (typeof value === 'object') {
        return value.id || fallback
      }

      return value
    },

    toLocalDateTime(value) {
      if (!value) {
        return ''
      }

      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return ''
      }

      const offsetMs = parsed.getTimezoneOffset() * 60000
      return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16)
    },

    formatPreviewDate(value) {
      if (!value) {
        return ''
      }

      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return ''
      }

      return new Intl.DateTimeFormat('es-EC', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsed)
    },

    computeEventEditableSignature(event) {
      const source = event || {}
      return JSON.stringify({
        name: source.name || '',
        description: source.description || '',
        type: Number(source.type || 1),
        access: Number(source.access || 1),
        start_date: this.toLocalDateTime(source.start_date),
        end_date: this.toLocalDateTime(source.end_date),
        category: this.extractRelationId(source.category_id, this.extractRelationId(source.category)),
        country: this.extractRelationId(source.country_id, this.extractRelationId(source.country)),
        city: this.extractRelationId(source.city_id, this.extractRelationId(source.city)),
        venue: this.extractRelationId(source.venue_id, this.extractRelationId(source.venue)),
        presentment_currency: source.presentment_currency || '',
        payout_mode: source.payout_policy && source.payout_policy.mode ? source.payout_policy.mode : 'after_event_plus_buffer',
        post_event_buffer_hours: source.payout_policy && source.payout_policy.post_event_buffer_hours ? Number(source.payout_policy.post_event_buffer_hours) : 24,
        trailer: source.trailer || '',
        meta_pixel: source.meta_pixel || '',
        message: source.message || '',
        show_end_date: Boolean(source.show_end_date),
        banner_url: source.banner_url || '',
      })
    },

    captureFormSignature() {
      return JSON.stringify({
        name: this.form.name || '',
        description: this.form.description || '',
        type: Number(this.form.type || 1),
        access: Number(this.form.access || 1),
        start_date: this.form.start_date || '',
        end_date: this.form.end_date || '',
        category: this.form.category || '',
        country: this.form.country || '',
        city: this.form.city || '',
        venue: this.form.venue || '',
        presentment_currency: this.form.presentment_currency || '',
        payout_mode: this.form.payout_mode || '',
        post_event_buffer_hours: Number(this.form.post_event_buffer_hours || 0),
        trailer: this.form.trailer || '',
        meta_pixel: this.form.meta_pixel || '',
        message: this.form.message || '',
        show_end_date: Boolean(this.form.show_end_date),
        removeBanner: this.removeBanner,
        bannerFile: this.form.banner ? `${this.form.banner.name}:${this.form.banner.size}` : '',
        bannerPreview: this.bannerPreview || '',
      })
    },

    syncSelectedVenue(preferredVenueId = '') {
      const candidateId = preferredVenueId || this.form.venue

      if (candidateId && this.venues.some((venue) => Number(venue.id) === Number(candidateId))) {
        this.form.venue = candidateId
        return
      }

      this.form.venue = ''
    },

    syncSelectedCity(preferredCityId = '') {
      const candidateId = preferredCityId || this.form.city

      if (candidateId && this.cities.some((city) => Number(city.id) === Number(candidateId))) {
        this.form.city = candidateId
        return
      }

      this.form.city = ''
      this.form.venue = ''
    },

    syncCurrencySelection() {
      const availableCodes = this.availableCurrencies.map((currency) => currency.code)
      if (availableCodes.includes(this.form.presentment_currency)) {
        return
      }
      const defaultCurrency = (this.selectedCountry && this.selectedCountry.default_currency) || (this.availableCurrencies[0] && this.availableCurrencies[0].code) || ''
      this.form.presentment_currency = defaultCurrency
    },

    fetchVenues(cityId, { preferredVenueId = '', force = false } = {}) {
      if (!cityId) {
        this.venues = []
        this.loadingVenues = false
        this.form.venue = ''
        return Promise.resolve([])
      }

      const cityKey = String(cityId)
      if (!force && this.venuesByCity[cityKey]) {
        this.venues = this.venuesByCity[cityKey]
        this.syncSelectedVenue(preferredVenueId)
        return Promise.resolve(this.venues)
      }

      const requestToken = this.venueRequestToken + 1
      this.venueRequestToken = requestToken
      this.loadingVenues = true

      return apiService(`/api/venues/?city_id=${encodeURIComponent(cityId)}`).then((data) => {
        if (requestToken !== this.venueRequestToken) {
          return []
        }

        const normalizedVenues = this.normalizeVenues(data)
        if (!(data && data.err)) {
          this.venuesByCity = {
            ...this.venuesByCity,
            [cityKey]: normalizedVenues,
          }
        }

        this.venues = normalizedVenues
        this.syncSelectedVenue(preferredVenueId)

        if (data && data.err) {
          this.errorMessage = this.extractErrorMessage(data)
        }

        return normalizedVenues
      }).finally(() => {
        if (requestToken === this.venueRequestToken) {
          this.loadingVenues = false
        }
      })
    },

    fetchCities(countryId, { preferredCityId = '' } = {}) {
      if (!countryId) {
        this.cities = []
        this.form.city = ''
        this.form.venue = ''
        return Promise.resolve([])
      }

      const requestToken = this.cityRequestToken + 1
      this.cityRequestToken = requestToken
      this.loadingOptions = true

      return apiService(`/api/cities/?country_id=${encodeURIComponent(countryId)}`).then((data) => {
        if (requestToken !== this.cityRequestToken) {
          return []
        }

        this.cities = this.normalizeCollection(data)
        this.syncSelectedCity(preferredCityId)
        return this.cities
      }).finally(() => {
        if (requestToken === this.cityRequestToken) {
          this.loadingOptions = false
        }
      })
    },

    bootstrapOptions() {
      this.loadingOptions = true
      Promise.allSettled([
        apiService('/api/category/'),
        apiService('/api/countries/'),
      ]).then(([categoriesResult, countriesResult]) => {
        this.categories = this.normalizeCollection(categoriesResult.value)
        this.countries = this.normalizeCountries(countriesResult.value)
        this.syncCurrencySelection()

        const failedBuckets = []
        if (this.categories.length === 0) {
          failedBuckets.push('categorias')
        }
        if (this.countries.length === 0) {
          failedBuckets.push('paises')
        }

        if (failedBuckets.length) {
          this.errorMessage = `No pudimos cargar ${failedBuckets.join(', ')}.`
        }
      }).finally(() => {
        this.loadingOptions = false
        if (this.form.country) {
          this.fetchCities(this.form.country, { preferredCityId: this.form.city }).then(() => {
            if (this.form.city) {
              this.fetchVenues(this.form.city, { preferredVenueId: this.form.venue, force: true })
            }
          })
        }
      })
    },

    hydrateFormFromEvent(event) {
      const source = event || {}

      this.revokeBannerPreview()
      this.form = {
        ...this.form,
        name: source.name || '',
        description: source.description || '',
        type: Number(source.type || 1),
        access: Number(source.access || 1),
        start_date: this.toLocalDateTime(source.start_date),
        end_date: this.toLocalDateTime(source.end_date),
        category: this.extractRelationId(source.category_id, this.extractRelationId(source.category)),
        country: this.extractRelationId(source.country_id, this.extractRelationId(source.country)),
        city: this.extractRelationId(source.city_id, this.extractRelationId(source.city)),
        venue: this.extractRelationId(source.venue_id, this.extractRelationId(source.venue)),
        presentment_currency: source.presentment_currency || '',
        payout_mode: source.payout_policy && source.payout_policy.mode ? source.payout_policy.mode : 'after_event_plus_buffer',
        post_event_buffer_hours: source.payout_policy && source.payout_policy.post_event_buffer_hours ? Number(source.payout_policy.post_event_buffer_hours) : 24,
        trailer: source.trailer || '',
        meta_pixel: source.meta_pixel || '',
        message: source.message || '',
        show_end_date: Boolean(source.show_end_date),
        banner: null,
      }
      this.bannerPreview = source.banner_url || ''
      this.removeBanner = false

      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.value = ''
      }

      this.syncCurrencySelection()

      if (this.form.country) {
        this.fetchCities(this.form.country, { preferredCityId: this.form.city }).then(() => {
          if (this.form.city) {
            this.fetchVenues(this.form.city, { preferredVenueId: this.form.venue })
          } else {
            this.venues = []
          }
        })
      } else {
        this.venues = []
      }

      this.syncedSignature = this.captureFormSignature()
    },

    handleCityChange() {
      this.form.venue = ''
      this.fetchVenues(this.form.city)
    },

    handleCountryChange() {
      this.form.city = ''
      this.form.venue = ''
      this.syncCurrencySelection()
      this.fetchCities(this.form.country)
    },

    triggerBannerPicker() {
      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.click()
      }
    },

    handleBannerDrag() {
      this.dragBannerActive = true
    },

    handleBannerDragLeave() {
      this.dragBannerActive = false
    },

    handleBannerDrop(event) {
      this.dragBannerActive = false
      const file = event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null
      if (!file) {
        return
      }

      this.attachBannerFile(file)
    },

    onBannerChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }

      this.attachBannerFile(file)
    },

    revokeBannerPreview() {
      if (this.bannerObjectUrl) {
        URL.revokeObjectURL(this.bannerObjectUrl)
        this.bannerObjectUrl = ''
      }
    },

    attachBannerFile(file) {
      if (!file.type || !file.type.startsWith('image/')) {
        this.errorMessage = 'La portada debe ser una imagen.'
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = 'La portada pesa demasiado. Usa una imagen menor a 10 MB.'
        return
      }

      this.revokeBannerPreview()
      this.form.banner = file
      this.removeBanner = false
      this.bannerObjectUrl = URL.createObjectURL(file)
      this.bannerPreview = this.bannerObjectUrl
      this.errorMessage = ''
    },

    clearBanner() {
      this.revokeBannerPreview()
      this.dragBannerActive = false
      this.bannerPreview = ''
      this.form.banner = null
      this.removeBanner = true
      if (this.$refs.bannerInput) {
        this.$refs.bannerInput.value = ''
      }
    },

    appendField(formData, field, value) {
      if (typeof value === 'boolean') {
        formData.append(field, value ? 'true' : 'false')
        return
      }

      formData.append(field, value == null ? '' : value)
    },

    buildPayload() {
      const payload = new FormData()
      const withSeconds = (value) => (value ? `${value}:00` : '')

      this.appendField(payload, 'name', this.form.name)
      this.appendField(payload, 'description', this.form.description)
      this.appendField(payload, 'type', this.form.type)
      this.appendField(payload, 'access', this.form.access)
      this.appendField(payload, 'start_date', withSeconds(this.form.start_date))
      this.appendField(payload, 'end_date', withSeconds(this.form.end_date))
      this.appendField(payload, 'category', this.form.category || '')
      this.appendField(payload, 'country', this.form.country || '')
      this.appendField(payload, 'city', this.form.city || '')
      this.appendField(payload, 'venue', this.form.venue || '')
      this.appendField(payload, 'presentment_currency', this.form.presentment_currency || '')
      this.appendField(payload, 'payout_mode', this.form.payout_mode)
      this.appendField(payload, 'post_event_buffer_hours', this.form.post_event_buffer_hours)
      this.appendField(payload, 'show_end_date', this.form.show_end_date)
      this.appendField(payload, 'meta_pixel', this.form.meta_pixel)
      this.appendField(payload, 'trailer', this.form.trailer)
      this.appendField(payload, 'message', this.form.message)

      if (this.form.banner) {
        payload.append('banner_url', this.form.banner)
      }

      if (this.removeBanner && !this.form.banner) {
        payload.append('remove_banner', 'true')
      }

      return payload
    },

    extractErrorMessage(data) {
      if (!data) {
        return 'No pudimos guardar los cambios del evento.'
      }

      if (data.err) {
        return data.err
      }

      const firstValue = typeof data === 'object' ? Object.values(data)[0] : null
      if (Array.isArray(firstValue)) {
        return firstValue[0]
      }

      return firstValue || 'No pudimos guardar los cambios del evento.'
    },

    humanizeField(fieldName) {
      return FIELD_LABELS[fieldName] || fieldName
    },

    formatFieldList(fieldNames) {
      const labels = fieldNames.map((fieldName) => this.humanizeField(fieldName))
      if (labels.length <= 1) {
        return labels[0] || 'este evento'
      }

      if (labels.length === 2) {
        return `${labels[0]} y ${labels[1]}`
      }

      return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`
    },

    buildSuccessMessage(payload) {
      const result = payload && payload.event_edit_result ? payload.event_edit_result : null
      const appliedFields = result && Array.isArray(result.applied_fields) ? result.applied_fields : []
      const sensitiveFields = result && Array.isArray(result.sensitive_fields_detected) ? result.sensitive_fields_detected : []

      if (appliedFields.length === 0) {
        return 'No habia cambios nuevos para guardar.'
      }

      if (result && result.approval_mode === 'auto' && sensitiveFields.length > 0) {
        return `Cambios guardados al instante. ${this.formatFieldList(sensitiveFields.slice(0, 3))} tambien quedaron activos sin esperar aprobacion.`
      }

      return `Cambios guardados al instante en ${this.formatFieldList(appliedFields.slice(0, 3))}.`
    },

    resetForm() {
      this.hydrateFormFromEvent(this.event)
      this.errorMessage = ''
      this.successMessage = ''
    },

    submitChanges() {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.isDirty) {
        this.successMessage = 'No hay cambios nuevos por guardar.'
        return
      }

      if (this.form.start_date && this.form.end_date && new Date(this.form.end_date) < new Date(this.form.start_date)) {
        this.errorMessage = 'La fecha de fin no puede ser anterior a la fecha de inicio.'
        return
      }

      this.saving = true
      apiServiceForm(`/api/host/events/${this.eventId}/`, 'PATCH', this.buildPayload()).then((data) => {
        if (data && !data.err && data.id) {
          this.successMessage = this.buildSuccessMessage(data)
          this.$emit('event-updated', data)
          return
        }

        this.errorMessage = this.extractErrorMessage(data)
      }).finally(() => {
        this.saving = false
      })
    },
  },
}
</script>

<style scoped>
.event-edit-shell {
  align-items: start;
}

.event-edit-panel,
.event-edit-preview {
  display: grid;
  gap: 18px;
}

.event-edit-panel__header {
  margin-bottom: 0;
}

.event-edit-policy-row {
  margin-top: -4px;
}

.event-edit-form {
  gap: 18px;
}

.event-edit-section {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 212, 224, 0.08);
}

.event-edit-section__header {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
}

.event-edit-section__header small {
  display: block;
  color: rgba(202, 214, 223, 0.7);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.event-edit-section__header strong {
  color: #eef4ff;
  font-size: 1.05rem;
}

.event-edit-section__tag {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  color: rgba(214, 225, 236, 0.88);
  font-size: 0.76rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(194, 212, 224, 0.1);
}

.event-edit-file-input {
  display: none;
}

.event-edit-banner {
  position: relative;
  overflow: hidden;
  display: grid;
  min-height: 280px;
  padding: 0;
  border: 1px solid rgba(194, 212, 224, 0.14);
  border-radius: 26px;
  background:
    radial-gradient(circle at top left, rgba(132, 229, 217, 0.14), transparent 22%),
    linear-gradient(180deg, rgba(8, 13, 19, 0.92), rgba(7, 9, 14, 0.98));
  cursor: pointer;
}

.event-edit-banner.has-image {
  min-height: 320px;
}

.event-edit-banner.is-dragging {
  border-color: rgba(140, 243, 232, 0.46);
  box-shadow: 0 0 0 1px rgba(140, 243, 232, 0.2), 0 24px 48px rgba(0, 0, 0, 0.18);
}

.event-edit-banner__image,
.event-edit-preview__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-edit-banner__placeholder,
.event-edit-preview__placeholder {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  padding: 28px;
  color: rgba(230, 238, 246, 0.82);
  text-align: center;
}

.event-edit-banner__placeholder i,
.event-edit-preview__placeholder i {
  font-size: 1.8rem;
}

.event-edit-banner__placeholder strong,
.event-edit-preview__placeholder strong {
  font-size: 1.05rem;
}

.event-edit-banner__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(6, 10, 14, 0.02), rgba(6, 10, 14, 0.7));
}

.event-edit-banner__copy {
  position: absolute;
  right: 18px;
  bottom: 18px;
  left: 18px;
  display: grid;
  gap: 4px;
  color: #eff7ff;
  text-align: left;
}

.event-edit-banner__copy span {
  font-size: 1rem;
  font-weight: 700;
}

.event-edit-banner__copy small {
  color: rgba(226, 236, 244, 0.78);
}

.event-edit-banner__actions {
  margin-top: 12px;
}

.event-edit-actions {
  margin-top: 4px;
}

.event-edit-preview__media {
  overflow: hidden;
  min-height: 220px;
  border-radius: 26px;
  border: 1px solid rgba(194, 212, 224, 0.12);
  background:
    radial-gradient(circle at top left, rgba(132, 229, 217, 0.16), transparent 24%),
    linear-gradient(180deg, rgba(9, 13, 19, 0.92), rgba(7, 10, 15, 0.98));
}

.event-edit-preview__media.is-empty {
  display: grid;
}

.event-edit-preview__copy {
  display: grid;
  gap: 10px;
}

.event-edit-preview__copy h3 {
  margin: 0;
  color: #eef4ff;
  font-family: 'Syne', sans-serif;
  font-size: clamp(1.6rem, 2.2vw, 2.2rem);
  line-height: 0.96;
}

.event-edit-preview__copy p,
.event-edit-preview__note p {
  margin: 0;
  color: rgba(222, 231, 241, 0.78);
  line-height: 1.7;
}

.event-edit-preview__note,
.event-edit-preview__fields {
  display: grid;
  gap: 10px;
  padding: 16px 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(194, 212, 224, 0.08);
}

.event-edit-preview__note strong,
.event-edit-preview__fields small {
  color: #eef4ff;
}

.event-edit-preview__fields small {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.event-edit-shell .workspace-button--ghost {
  background: rgba(11, 19, 24, 0.9) !important;
  border-color: rgba(194, 212, 224, 0.12) !important;
}

.event-edit-shell :deep(.workspace-field__note) {
  color: rgba(208, 219, 238, 0.68);
  line-height: 1.5;
}

@media (max-width: 980px) {
  .event-edit-shell {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .event-edit-section {
    padding: 16px;
  }

  .event-edit-section__header,
  .event-edit-actions {
    display: grid;
  }

  .event-edit-banner,
  .event-edit-preview__media {
    min-height: 220px;
  }
}
</style>
